@let isOwnershipStakeMissing = (isOwnershipStakeMissing$ | async);
@let isEditDisabled = (isEditDisabled$ | async);
@let hasFundInvestmentNav = navExists(fundInvestmentNav$ | async);
@let pending = pendingHistoryBaseChange;
@let pendingRemoves = !!pending && pending.kind === 'DELETE';

<mat-card class="detail-card">
    @if (!hasFundInvestmentNav) {
        <valumize-info-panel
            [appearance]="'WARNING'" [message]="'GP NAV is missing. Please add a valid amount for NAV first.'"></valumize-info-panel>
    }
    @if (isOwnershipStakeMissing) {
        <valumize-info-panel
            [appearance]="'WARNING'" [message]="'Ownership Stake is missing. Please add an ownership stake for this investment on the fund page first.'"></valumize-info-panel>
    }
    @if ((financialOperationalValuation$ | async); as financialOperationalValuation) {
        @let historyBase = financialOperationalValuation.assetValuation.data.historyBase;
        @let isHistoryBaseMissing = pendingRemoves || (!pending && !historyBase);
        <mat-card-subtitle>
            @if (isHistoryBaseMissing && financialOperationalValuation.assetValuation.data.id && hasRequiredFieldsExceptHistoryBase(financialOperationalValuation.assetValuation.data)) {
                <valumize-info-panel [appearance]="'WARNING'"
                                     [message]="'History base period is missing. Please select one to run FOV calculations.'"></valumize-info-panel>
            }
            @if (isNetDebtAssumedZero(historyBase)) {
                <valumize-info-panel [appearance]="'WARNING'"
                                     [message]="netDebtAssumedZeroMessage"
                                     data-test="net-debt-assumed-zero-banner"></valumize-info-panel>
            }
            @if (fovError$ | async; as fovError) {
                <valumize-info-panel [appearance]="'ERROR'" [message]="fovError"></valumize-info-panel>
            }
            @if (financialOperationalValuation.assetValuation.status === 'SAVED') {
                <valumize-info-panel [appearance]="'SAVED'"
                                     [message]="'Financial Operational Valuation successfully saved'"></valumize-info-panel>
            }
            @if (financialOperationalValuation.assetValuation.status === 'LOADING') {
                <mat-progress-spinner></mat-progress-spinner>
            }
        </mat-card-subtitle>
        @if (financialOperationalValuation.assetValuation.data.id) {
            <mat-card-content class="detail-card-content">
                <form [formGroup]="operationalValuationForm">
                    <div class="key-value-pair-section">
                        <div class="key-value-pair-column">
                            <div>
                                <div class="key-value-pair">
                                    <div class="key-value-item">Earning Base</div>
                                    <div class="key-value-item" [ngClass]="{'bg-warning': !financialOperationalValuation.assetValuation.data.earningBase.code}">
                                        @if (!financialOperationalValuation.isEditable.valuationIsEditable) {
                                            <div>
                                                {{ financialOperationalValuation.assetValuation.data.earningBase.code | codeFormat: codeTableEarningBase | async }}
                                            </div>
                                        }
                                        @if (financialOperationalValuation.isEditable.valuationIsEditable) {
                                            <div>
                                                <select formControlName="earningBase">
                                                    @for (earningBase of (selectCodesEarningBase$ | async); track earningBase) {
                                                        <option
                                                            [value]="earningBase.code">{{ earningBase.translations?.["en"] }}
                                                        </option>
                                                    }
                                                </select>
                                            </div>
                                        }
                                    </div>
                                </div>
                                <div class="key-value-pair">
                                    <div class="key-value-item">Exit Date Assumption</div>
                                    <div class="key-value-item" [ngClass]="{'bg-warning': !financialOperationalValuation.assetValuation.data.exitDateAssumption.date}">
                                        @if (!financialOperationalValuation.isEditable.valuationIsEditable) {
                                            <div>
                                                {{ financialOperationalValuation.assetValuation.data.exitDateAssumption | traceableFormat }}
                                            </div>
                                        }
                                        @if (financialOperationalValuation.isEditable.valuationIsEditable) {
                                            <mat-form-field class="input-date-narrow">
                                                <input matInput
                                                       formControlName="exitDateAssumption"
                                                       id="exitDateAssumption"
                                                       [matDatepicker]="exitDatePicker">
                                                <mat-hint>dd/MM/yyyy</mat-hint>
                                                <mat-datepicker-toggle matIconSuffix [for]="exitDatePicker"></mat-datepicker-toggle>
                                                <mat-datepicker #exitDatePicker></mat-datepicker>
                                            </mat-form-field>
                                        }
                                    </div>
                                </div>
                                <div class="key-value-pair">
                                    <div class="key-value-item">History Base Period</div>
                                    <div class="key-value-item"
                                         [ngClass]="{'bg-warning': isHistoryBaseMissing}">
                                        @if (pending) {
                                            <div class="history-base-value">
                                                <span>{{ pendingRemoves ? '-' : pending.previewLabel }}</span>
                                                @if (financialOperationalValuation.isEditable.valuationIsEditable) {
                                                    <button mat-icon-button (click)="clearPendingHistoryBaseChange()"
                                                            class="icon-button-sm"
                                                            aria-label="Undo pending history base change">
                                                        <mat-icon class="icon-sm">undo</mat-icon>
                                                    </button>
                                                }
                                            </div>
                                        } @else if (historyBase) {
                                            <div class="history-base-value">
                                                <span>{{ getHistoryBaseLabel(historyBase) }}</span>
                                                @if (isCustomHistoryBase(historyBase) && historyBase.period.keyDate) {
                                                    <span class="history-base-date">{{ historyBase.period!.keyDate | date:'d MMM yyyy' }}</span>
                                                }
                                                @if (historyBase.stale) {
                                                    <mat-icon color="warn" class="icon-sm"
                                                              matTooltip="The source record has changed since this copy was made.">warning
                                                    </mat-icon>
                                                }
                                                @if (financialOperationalValuation.isEditable.valuationIsEditable) {
                                                    <button mat-icon-button color="warn" (click)="onDeleteHistoryBase()"
                                                            class="icon-button-sm"
                                                            aria-label="Remove history base period">
                                                        <mat-icon class="icon-sm">close</mat-icon>
                                                    </button>
                                                }
                                            </div>
                                        } @else if (!financialOperationalValuation.isEditable.valuationIsEditable) {
                                            <span>-</span>
                                        }
                                        @if (financialOperationalValuation.isEditable.valuationIsEditable && isHistoryBaseMissing) {
                                            <mat-select class="history-record-select" placeholder="Select period..."
                                                        panelClass="history-record-select-panel"
                                                        [value]="historyRecordSelectValue"
                                                        (selectionChange)="onHistoryRecordSelected($event.value)">
                                                @for (record of (availableHistoryRecords$ | async); track record.id) {
                                                    @let selectable = isHistoryRecordSelectable(record);
                                                    <mat-option [value]="record.id" [disabled]="!selectable">
                                                        <span class="history-record-option">
                                                            {{ getHistoryRecordLabel(record) }}
                                                            @if (!selectable) {
                                                                <mat-icon color="warn" class="history-record-info-icon"
                                                                          [matTooltip]="historyRecordIncompleteMessage"
                                                                          matTooltipPosition="right" #tt="matTooltip"
                                                                          (click)="$event.stopPropagation(); tt.toggle()">info</mat-icon>
                                                            } @else if (isHistoryRecordEnterpriseValueMissing(record)) {
                                                                <mat-icon color="warn" class="history-record-info-icon"
                                                                          [matTooltip]="historyRecordEnterpriseValueMissingMessage"
                                                                          matTooltipPosition="right" #evTt="matTooltip"
                                                                          (click)="$event.stopPropagation(); evTt.toggle()">info</mat-icon>
                                                            }
                                                        </span>
                                                    </mat-option>
                                                }
                                                <mat-option value="__custom__">--- Enter custom record ---</mat-option>
                                            </mat-select>
                                        }
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="key-value-pair-column">
                            <div>
                                <div class="key-value-pair">
                                    <div class="key-value-item">Required gIRR</div>
                                    <div class="key-value-item" [ngClass]="{'bg-warning': !financialOperationalValuation.assetValuation.data.requiredGrossIRR.fraction}">
                                        @let requiredGrossIRRSource = financialOperationalValuation.assetValuation.data.requiredGrossIRR.source;
                                        @if (!financialOperationalValuation.isEditable.valuationIsEditable) {
                                            <span class="value-with-gear">
                                                <valumize-source-gear [source]="requiredGrossIRRSource"></valumize-source-gear>
                                                {{ financialOperationalValuation.assetValuation.data.requiredGrossIRR | traceableFormat }}
                                            </span>
                                        }
                                        @if (financialOperationalValuation.isEditable.valuationIsEditable) {
                                            <span class="value-with-gear">
                                                <valumize-source-gear [source]="requiredGrossIRRSource"></valumize-source-gear>
                                                <input matInput
                                                       class="input-number-narrow"
                                                       type="number"
                                                       formControlName="requiredGrossIRR"
                                                       id="requiredGrossIRR">
                                            </span>
                                        }
                                    </div>
                                </div>
                                <div class="key-value-pair">
                                    <div class="key-value-item">Exchange Rate to Fund</div>
                                    <div class="key-value-item" [ngClass]="{'bg-warning': !financialOperationalValuation.assetValuation.data.fxRateFund.fraction}">
                                        @let fxSource = financialOperationalValuation.assetValuation.data.fxRateFund.source;
                                        @if (!financialOperationalValuation.isEditable.valuationIsEditable) {
                                            <div>
                                                @if (fxSource?.startsWith("ECB ")) {
                                                    <span [matTooltip]="'Live ECB rate as of ' + ((fxSource || '').slice(4) | date)">
                                                        {{ financialOperationalValuation.assetValuation.data.fxRateFund.fraction | decimalFormat: "number" }}
                                                    </span>
                                                } @else {
                                                    {{ financialOperationalValuation.assetValuation.data.fxRateFund.fraction | decimalFormat: "number" }}
                                                }
                                            </div>
                                        }
                                        @if (financialOperationalValuation.isEditable.valuationIsEditable) {
                                            @let af = (fxAutofill$ | async);
                                            <div>
                                                <input matInput
                                                       class="input-number-narrow"
                                                       type="number"
                                                       formControlName="fxRateFund"
                                                       id="fxRateFund">
                                                <button mat-icon-button
                                                        type="button"
                                                        matTooltip="Reload latest ECB rate"
                                                        (click)="refreshFxRate()">
                                                    <mat-icon>sync</mat-icon>
                                                </button>
                                            </div>
                                            @if (fxAutofillAsOfDate) {
                                                @if (fxAutofilledUntouched) {
                                                    <div class="fx-asof">Live rate as of {{ fxAutofillAsOfDate | date }}</div>
                                                } @else {
                                                    <div class="fx-asof">User edited on {{ fxEditedDate | date }}</div>
                                                }
                                            }
                                            @if (af?.status === "ERROR" || (af?.status === "LOADED" && af?.data?.fraction === null)) {
                                                <div class="fx-error">Could not load a live exchange rate for this currency pair.</div>
                                            }
                                        }
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="add-cashflow-container">
                            <button (click)="openNewCashflowDialog(financialOperationalValuation.scenarios)"
                                    [disabled]="isEditDisabled! || !validateRequiredFieldsForCalc(financialOperationalValuation.assetValuation.data)"
                                    class="add-cashflow-button"
                                    color="primary"
                                    mat-raised-button>
                                Add New Cashflow
                            </button>
                            @if (notAssignedCapex$ | async; as capex) {
                                @if (capex.LOW !== 0 || capex.BASE !== 0 || capex.HIGH !== 0) {
                                    <mat-icon
                                        color="warn"
                                        [matTooltip]="'Unassigned Capex Drawdown:' +
                                          '\nLow: ' + (capex.LOW | decimalFormat:'currency') +
                                          '\nBase: ' + (capex.BASE | decimalFormat:'currency') +
                                          '\nHigh: ' + (capex.HIGH | decimalFormat:'currency')"
                                        matTooltipClass="custom-tooltip">
                                        info_outline
                                    </mat-icon>
                                }
                            }
                        </div>
                    </div>
                </form>
                @if (validateRequiredFieldsForCalc(financialOperationalValuation.assetValuation.data)) {
                    <div class="key-value-pair-section">
                        <div class="key-value-pair-column">
                            <div>
                                <h3 data-test="subtitle-dcf">DCF Valuation Analysis</h3>
                                <div class="key-value-pair">
                                    <div class="key-value-item">Wgt Holding Period (yrs)</div>
                                    <div class="key-value-item">{{ financialOperationalValuation.assetValuation.data.weightedHoldingPeriod | decimalFormat:'number' }}</div>
                                </div>
                                <div class="key-value-pair">
                                    <div class="key-value-item">Exit Proceeds</div>
                                    <div class="key-value-item">{{ financialOperationalValuation.assetValuation.data.exitProceeds | traceableFormat }}</div>
                                </div>
                                <div class="key-value-pair">
                                    <div class="key-value-item">Exit Proceeds - Fund Currency</div>
                                    <div class="key-value-item">{{ financialOperationalValuation.assetValuation.data.exitProceedsFundCurrency | traceableFormat }}</div>
                                </div>
                                <div class="key-value-pair">
                                    <div class="key-value-item">Bid Price - Fund Currency</div>
                                    <div class="key-value-item">{{ financialOperationalValuation.assetValuation.data.finalBidPrice | traceableFormat }}</div>
                                </div>
                            </div>
                        </div>
                        <div class="key-value-pair-column">
                            <div>
                                <h3 data-test="subtitle-purchase-multiples">Purchase Multiples</h3>
                                @if (financialOperationalValuation.assetValuation.data.earningBase.code; as earningBase) {
                                    <div class="key-value-pair">
                                        <div class="key-value-item">
                                            EV/{{ earningBase | codeFormat: codeTableEarningBase | async }}
                                        </div>
                                        <div class="key-value-item">{{ financialOperationalValuation.assetValuation.data.enterpriseValueOverEarningBaseMultiple | traceableFormat }}
                                        </div>
                                    </div>
                                }
                                <div class="key-value-pair">
                                    <div class="key-value-item">EV/Revenues</div>
                                    <div class="key-value-item">{{ financialOperationalValuation.assetValuation.data.enterpriseValueOverRevenueMultiple | traceableFormat }}</div>
                                </div>
                            </div>
                        </div>
                        <div class="key-value-pair-column">
                            <div>
                                <h3 data-test="subtitle-purchase-multiples">Performance Summary</h3>
                                <div class="key-value-pair">
                                    <div class="key-value-item">NAV Multiple</div>
                                    <div class="key-value-item">{{ financialOperationalValuation.assetValuation.data.navMultiple | traceableFormat }}</div>
                                </div>
                                <div class="key-value-pair">
                                    <div class="key-value-item">Secondary TVPI</div>
                                    <div class="key-value-item">{{ financialOperationalValuation.assetValuation.data.secondaryTVPI | traceableFormat }}</div>
                                </div>
                                <div class="key-value-pair">
                                    <div class="key-value-item">Secondary IRR</div>
                                    <div class="key-value-item">{{ financialOperationalValuation.assetValuation.data.secondaryIRR | traceableFormat }}</div>
                                </div>
                            </div>
                        </div>
                        <div class="key-value-pair-column">
                            <div>
                                <h3 data-test="subtitle-purchase-multiples">Pricing Summary</h3>
                                <div class="key-value-pair">
                                    <div class="key-value-item">Discount to GP NAV</div>
                                    <div class="key-value-item">{{ financialOperationalValuation.assetValuation.data.discountToGpNAV | traceableFormat }}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <mat-accordion multi>
                        @if (findScenario(financialOperationalValuation.scenarios, 'LOW'); as scenarioLow) {
                            <valumize-asset-financial-operational-valuation-scenario
                                [assetId]="assetId"
                                [assetValuationId]="financialOperationalValuation.assetValuation.data.id"
                                [scenario]="scenarioLow"
                                [cashflowsAreEditable]="financialOperationalValuation.isEditable.lowCaseIsEditable.cashflowsAreEditable"
                                [recordsAreEditable]="financialOperationalValuation.isEditable.lowCaseIsEditable.recordsAreEditable"
                                [earningBase]="financialOperationalValuation.assetValuation.data.earningBase"
                                [historyBase]="financialOperationalValuation.assetValuation.data.historyBase">
                            </valumize-asset-financial-operational-valuation-scenario>
                        }
                        @if (findScenario(financialOperationalValuation.scenarios, 'BASE'); as scenarioBase) {
                            <valumize-asset-financial-operational-valuation-scenario
                                [assetId]="assetId"
                                [assetValuationId]="financialOperationalValuation.assetValuation.data.id"
                                [scenario]="scenarioBase"
                                [cashflowsAreEditable]="financialOperationalValuation.isEditable.baseCaseIsEditable.cashflowsAreEditable"
                                [recordsAreEditable]="financialOperationalValuation.isEditable.baseCaseIsEditable.recordsAreEditable"
                                [earningBase]="financialOperationalValuation.assetValuation.data.earningBase"
                                [historyBase]="financialOperationalValuation.assetValuation.data.historyBase">
                            </valumize-asset-financial-operational-valuation-scenario>
                        }
                        @if (findScenario(financialOperationalValuation.scenarios, 'HIGH'); as scenarioHigh) {
                            <valumize-asset-financial-operational-valuation-scenario
                                [assetId]="assetId"
                                [assetValuationId]="financialOperationalValuation.assetValuation.data.id"
                                [scenario]="scenarioHigh"
                                [cashflowsAreEditable]="financialOperationalValuation.isEditable.highCaseIsEditable.cashflowsAreEditable"
                                [recordsAreEditable]="financialOperationalValuation.isEditable.highCaseIsEditable.recordsAreEditable"
                                [earningBase]="financialOperationalValuation.assetValuation.data.earningBase"
                                [historyBase]="financialOperationalValuation.assetValuation.data.historyBase">
                            </valumize-asset-financial-operational-valuation-scenario>
                        }
                    </mat-accordion>
                }
            </mat-card-content>
            <mat-card-actions class="detail-card-actions">
                @if (!financialOperationalValuation.isEditable.valuationIsEditable && (isOwnershipStakeMissing === false)) {
                    <button (click)="editMode()"
                            [disabled]="isEditDisabled!"
                            [hidden]="!hasFundInvestmentNav"
                            color="primary"
                            data-test="edit-button"
                            mat-icon-button>
                        <mat-icon>edit</mat-icon>
                    </button>
                    @if (financialOperationalValuation.assetValuation.data.id) {
                        <button (click)="onResetValuation()"
                                [disabled]="isEditDisabled!"
                                color="primary"
                                data-test="reset-button"
                                mat-icon-button
                                aria-label="Reset valuation"
                                matTooltip="Reset valuation">
                            <mat-icon>restart_alt</mat-icon>
                        </button>
                    }
                }
                @if (financialOperationalValuation.isEditable.valuationIsEditable) {
                    <button (click)="save()"
                            [disabled]="operationalValuationForm.invalid"
                            color="primary"
                            data-test="save-button"
                            mat-icon-button>
                        <mat-icon>save</mat-icon>
                    </button>
                }
                @if (financialOperationalValuation.isEditable.valuationIsEditable) {
                    <button (click)="cancel()"
                            color="primary"
                            data-test="cancel-button"
                            mat-icon-button>
                        <mat-icon>cancel</mat-icon>
                    </button>
                }
            </mat-card-actions>
            <valumize-asset-valuation-rationale
                [assetValuation]="financialOperationalValuation.assetValuation.data"
                [rationaleIsEditable]="financialOperationalValuation.isEditable.operationalRationaleIsEditable"
                (saveRationale)="onSaveRationale(financialOperationalValuation.assetValuation.data, $event)"
                (editRationale)="onEditRationale()"
                (cancelRationale)="onCancelRationale()">
            </valumize-asset-valuation-rationale>
        }
    }
</mat-card>
